import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import SurveyModal from 'src/components/shared/SurveyModal';
import { createRegistration } from 'src/generated/api';
import { getTeacherPostRegistrationSurveyUrl, getRegistrationDoneUrl } from 'src/config';
import { submitRegistrationSurveyResponses } from 'src/api/registrationSurveyResponses';
import { RegistrationSuccess as SharedRegistrationSuccess } from 'shared/components/RegistrationSuccess';
import 'shared/styles/RegistrationSuccess.css';
import styles from 'src/components/flows/styles/RegistrationSuccess.module.scss';
/* eslint-enable no-unused-vars, react/require-default-props, react/no-unused-prop-types */
/**
 * RegistrationSuccess step component
 * Displays success message with join code and impact tracker code after registration
 * Now uses the shared RegistrationSuccess component
 */
const RegistrationSuccess = ({ initialData, setTitle, onPageValid, onHideNavigation, userType, registrationData, }) => {
    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [registration, setRegistration] = useState(null);
    const submittedRef = useRef(false);
    // Survey modal state
    const [showSurveyModal, setShowSurveyModal] = useState(false);
    // Get data from either initialData or registration state
    const schoolName = initialData?.schoolName;
    const joinCode = initialData?.joinCode || registration?.joinCode;
    /**
     * Submit registration when component mounts if registrationData is provided
     */
    useEffect(() => {
        const submitRegistration = async () => {
            // Skip if we already have success data
            if (initialData?.joinCode) {
                setTitle?.('Congrats!');
                onPageValid?.(true);
                onHideNavigation?.(true);
                return;
            }
            // Skip if no registration data
            if (!registrationData) {
                setError('Missing registration data');
                setTitle?.('Registration Error');
                onPageValid?.(false);
                onHideNavigation?.(false);
                return;
            }
            // Prevent double submission (Strict Mode)
            if (submittedRef.current) {
                return;
            }
            submittedRef.current = true;
            setLoading(true);
            setError(null);
            onHideNavigation?.(true);
            try {
                const response = await createRegistration(registrationData);
                setRegistration(response.data);
                setTitle?.('Congrats!');
                onPageValid?.(true);
                onHideNavigation?.(true);
            }
            catch (err) {
                setError(err instanceof Error
                    ? err.message
                    : 'Failed to complete registration. Please try again.');
                setTitle?.('Registration Error');
                onPageValid?.(false);
                onHideNavigation?.(false);
            }
            finally {
                setLoading(false);
            }
        };
        submitRegistration();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);
    /**
     * Handles the Done button click
     */
    const handleDone = () => {
        // Independent learners skip the survey and go straight to the done URL
        if (userType === 'independent') {
            window.location.href = getRegistrationDoneUrl();
            return;
        }
        // For school and community, show the survey modal
        setShowSurveyModal(true);
    };
    /**
     * Handles survey submission
     */
    const handleSurveySubmit = async (answers) => {
        if (!joinCode) {
            throw new Error('Missing join code. Please retry registration.');
        }
        await submitRegistrationSurveyResponses({
            joinCode,
            surveyUrl: getTeacherPostRegistrationSurveyUrl(),
            answers,
        });
        setShowSurveyModal(false);
        window.location.href = getRegistrationDoneUrl();
    };
    /**
     * Handles "Not at this time" button click in survey modal
     */
    const handleSkipSurvey = () => {
        setShowSurveyModal(false);
        window.location.href = getRegistrationDoneUrl();
    };
    // Loading state
    if (loading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.content, children: _jsx("div", { className: styles.loadingContainer, children: _jsx("p", { className: styles.loadingText, children: "Submitting your registration..." }) }) }) }));
    }
    // Error state
    if (error) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.content, children: _jsxs("div", { className: styles.errorContainer, children: [_jsx("p", { className: styles.errorText, children: error }), _jsx("p", { className: styles.errorHelp, children: "Please use the Back button to return and try again." })] }) }) }));
    }
    // Success state - use shared component
    const fullName = registrationData?.firstName && registrationData?.lastName
        ? `${registrationData.firstName} ${registrationData.lastName}`
        : undefined;
    // Extract parentOrg and otherParentOrg from eventData for community registrations
    const parentOrg = registrationData?.eventData?.parentOrg;
    const otherParentOrg = registrationData?.eventData?.otherParentOrg;
    return (_jsxs(_Fragment, { children: [_jsx(SharedRegistrationSuccess, { registrationType: userType || 'school', joinCode: joinCode || '', schoolName: schoolName, parentOrg: parentOrg, otherParentOrg: otherParentOrg, fullName: fullName, onDone: handleDone, showSurveyButton: true }), _jsx(SurveyModal, { isOpen: showSurveyModal, onClose: () => setShowSurveyModal(false), surveyUrl: getTeacherPostRegistrationSurveyUrl(), onSubmit: handleSurveySubmit, onSkip: handleSkipSurvey })] }));
};
export default RegistrationSuccess;
